import {useCallback, useEffect, useRef, useState} from 'react';
import mpegts from 'mpegts.js';
import PropTypes from 'prop-types';
import PlayerChrome from 'components/channels/PlayerChrome';
import useAudioTracks from 'hooks/useAudioTracks';
import {
  appendVid,
  computeBackoffDelay,
  createWatchdog,
  evaluateWatchdog,
  nextAttempt,
  STALL_THRESHOLD_MS,
  STARTUP_TIMEOUT_MS
} from 'utils/antiCutEngine';
import {createMpegAudioLoader} from 'utils/audioTrackLoaders';
import {playerHealth} from 'utils/playerHealth';
import {classifyPlaybackFailure, PLAYER_STATES} from 'utils/playerPlayback';

const INITIAL_CONNECT_DELAY_MS = 800;
const RECOVERED_FLASH_MS = 2500;
const WATCHDOG_INTERVAL_MS = 1000;

function browserIsOnline() {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function mediaFailureReason(value) {
  let summary = '';
  try {
    summary = JSON.stringify(value).toLowerCase();
  } catch (_) {
    summary = String(value || '').toLowerCase();
  }
  return /format|mime|demux|codec|media_source/.test(summary) ? 'transport' : 'network';
}

function AntiCutPlayer({
  playURL,
  playingName,
  variant,
  viewId,
  socket,
  bindPlayer,
  bindVideoElement,
  userPaused,
  onUserPausedChange,
  onRefreshVariant
}) {
  const [playback, setPlayback] = useState({
    status: PLAYER_STATES.PREPARING,
    attempt: 0,
    retryAt: null,
    reason: null
  });
  const [online, setOnline] = useState(browserIsOnline);

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const {audio, audioTracks, selectedAudioTrack} = useAudioTracks(playURL, videoRef);
  const playerRef = useRef(null);
  const playbackRef = useRef(playback);
  const watchdogRef = useRef(null);
  const watchdogTimerRef = useRef(null);
  const initialTimerRef = useRef(null);
  const restartTimerRef = useRef(null);
  const recoveredTimerRef = useRef(null);
  const retryReasonRef = useRef('network');
  const attemptRef = useRef(0);
  const healthySinceRef = useRef(null);
  const onlineRef = useRef(browserIsOnline());
  const userPausedRef = useRef(userPaused);
  const autoplayBlockedRef = useRef(false);
  const deferredFailureRef = useRef(null);
  const needsRecoveryConfirmationRef = useRef(false);
  const engineGenerationRef = useRef(0);
  const sessionGenerationRef = useRef(0);
  const createEngineRef = useRef(() => {});
  const scheduleRestartRef = useRef(() => {});
  const restartNowRef = useRef(() => {});

  useEffect(() => {
    playbackRef.current = playback;
  }, [playback]);

  useEffect(() => {
    userPausedRef.current = userPaused;
    if (
      userPaused &&
      playbackRef.current.status !== PLAYER_STATES.OFFLINE &&
      playbackRef.current.status !== PLAYER_STATES.TERMINAL_ERROR
    ) {
      const next = {...playbackRef.current, status: PLAYER_STATES.PAUSED, reason: null};
      playbackRef.current = next;
      setPlayback(next);
    }
  }, [userPaused]);

  const updatePlayback = useCallback(
    (patch) => {
      const previous = playbackRef.current;
      const next = {...previous, ...patch};
      if (patch.status && patch.status !== PLAYER_STATES.RECOVERED && recoveredTimerRef.current) {
        window.clearTimeout(recoveredTimerRef.current);
        recoveredTimerRef.current = null;
      }
      playbackRef.current = next;
      setPlayback(next);
      if (next.status !== previous.status) {
        playerHealth.report('player.stateChanged', {
          from: previous.status,
          to: next.status,
          variant,
          reason: next.reason || null
        });
      }
    },
    [variant]
  );

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current) window.clearTimeout(restartTimerRef.current);
    restartTimerRef.current = null;
  }, []);

  const destroyPlayer = useCallback(() => {
    engineGenerationRef.current += 1;
    const player = playerRef.current;
    playerRef.current = null;
    if (player) {
      try {
        player.destroy();
      } catch (_) {
        // A half-created mpegts instance is already unusable.
      }
    }
  }, []);

  const deferFailureWhilePaused = useCallback(
    (failure) => {
      clearRestartTimer();
      deferredFailureRef.current = {
        retryable: failure?.retryable !== false,
        reason: failure?.reason || 'network',
        statusCode: failure?.statusCode ?? null
      };
      destroyPlayer();
      updatePlayback({status: PLAYER_STATES.PAUSED, retryAt: null, reason: null});
    },
    [clearRestartTimer, destroyPlayer, updatePlayback]
  );

  const showRecovered = useCallback(() => {
    if (recoveredTimerRef.current) window.clearTimeout(recoveredTimerRef.current);
    const sessionGeneration = sessionGenerationRef.current;
    needsRecoveryConfirmationRef.current = false;
    updatePlayback({status: PLAYER_STATES.RECOVERED, retryAt: null, reason: null});
    playerHealth.report('player.recovered', {variant, attempt: attemptRef.current});
    playerHealth.report('anticut.recovered', {attempt: attemptRef.current});
    Promise.resolve(onRefreshVariant?.('recovered')).catch(() => {});
    recoveredTimerRef.current = window.setTimeout(() => {
      recoveredTimerRef.current = null;
      if (
        sessionGeneration !== sessionGenerationRef.current ||
        playbackRef.current.status !== PLAYER_STATES.RECOVERED
      )
        return;
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, reason: 'offline'});
      } else if (userPausedRef.current || autoplayBlockedRef.current) {
        updatePlayback({
          status: PLAYER_STATES.PAUSED,
          reason: autoplayBlockedRef.current ? 'autoplayBlocked' : null
        });
      } else {
        updatePlayback({status: PLAYER_STATES.PLAYING, reason: null});
      }
    }, RECOVERED_FLASH_MS);
  }, [onRefreshVariant, updatePlayback, variant]);

  const handleAutoplayBlocked = useCallback(() => {
    autoplayBlockedRef.current = true;
    updatePlayback({status: PLAYER_STATES.PAUSED, retryAt: null, reason: 'autoplayBlocked'});
    playerHealth.report('player.autoplayBlocked', {variant});
  }, [updatePlayback, variant]);

  const makeTerminal = useCallback(
    (classification) => {
      clearRestartTimer();
      deferredFailureRef.current = null;
      retryReasonRef.current = classification.reason;
      updatePlayback({
        status: PLAYER_STATES.TERMINAL_ERROR,
        retryAt: null,
        reason: classification.reason
      });
      // Publish the terminal state before destroy: media libraries may emit
      // late close/error callbacks while detaching the current generation.
      destroyPlayer();
      playerHealth.report('player.terminalError', {
        variant,
        reason: classification.reason,
        statusCode: classification.statusCode
      });
    },
    [clearRestartTimer, destroyPlayer, updatePlayback, variant]
  );

  const createEngine = useCallback(
    (url) => {
      if (!url || !onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }

      clearRestartTimer();
      destroyPlayer();
      const video = videoRef.current;
      if (!video) return;
      const generation = engineGenerationRef.current;
      let player;
      const isCurrent = () =>
        engineGenerationRef.current === generation && playerRef.current === player;

      updatePlayback({status: PLAYER_STATES.CONNECTING, retryAt: null, reason: null});
      autoplayBlockedRef.current = false;
      watchdogRef.current = createWatchdog(Date.now());

      try {
        player = mpegts.createPlayer(
          {type: 'mse', isLive: true, url: appendVid(url, viewId)},
          {
            enableWorker: false, // custom loader owns the audio selection on the main thread
            customLoader: createMpegAudioLoader(mpegts.BaseLoader, audio, isCurrent),
            lazyLoadMaxDuration: 3 * 60,
            seekType: 'range',
            liveBufferLatencyChasing: true,
            liveBufferLatencyMaxLatency: 10,
            liveBufferLatencyMinRemain: 5,
            autoCleanupSourceBuffer: true,
            autoCleanupMinBackwardDuration: 30,
            autoCleanupMaxBackwardDuration: 60
          }
        );
        playerRef.current = player;

        player.on(mpegts.Events.MEDIA_INFO, () => {
          if (!isCurrent()) return;
          if (
            playbackRef.current.status === PLAYER_STATES.PLAYING ||
            playbackRef.current.status === PLAYER_STATES.RECOVERED
          ) {
            return;
          }
          if (userPausedRef.current || autoplayBlockedRef.current) {
            updatePlayback({
              status: PLAYER_STATES.PAUSED,
              reason: autoplayBlockedRef.current ? 'autoplayBlocked' : null
            });
            return;
          }
          updatePlayback({
            status: PLAYER_STATES.STABILIZING,
            reason: null
          });
        });
        player.on(mpegts.Events.ERROR, (...args) => {
          if (!isCurrent()) return;
          if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
          const reason = mediaFailureReason(args);
          const classification = classifyPlaybackFailure(args, reason);
          playerHealth.report('anticut.mpegtsError', {
            reason,
            statusCode: classification.statusCode
          });
          if (userPausedRef.current) {
            deferFailureWhilePaused(classification);
            return;
          }
          if (reason === 'transport')
            Promise.resolve(onRefreshVariant?.('formatMismatch')).catch(() => {});
          if (!classification.retryable) makeTerminal(classification);
          else scheduleRestartRef.current(classification.reason);
        });
        player.on(mpegts.Events.LOADING_COMPLETE, () => {
          if (!isCurrent()) return;
          if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
          playerHealth.report('anticut.loadingComplete', {});
          if (userPausedRef.current) {
            deferFailureWhilePaused({retryable: true, reason: 'loadingComplete'});
            return;
          }
          scheduleRestartRef.current('loadingComplete');
        });

        player.attachMediaElement(video);
        bindPlayer?.(player);
        bindVideoElement?.(video);
        player.load();
        playerHealth.report('anticut.playerCreated', {
          attempt: attemptRef.current,
          variant,
          generation
        });

        if (userPausedRef.current) {
          video.pause();
          updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
        } else {
          const playPromise = player.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
              if (isCurrent()) handleAutoplayBlocked();
            });
          }
        }
      } catch (error) {
        if (engineGenerationRef.current !== generation) return;
        const classification = classifyPlaybackFailure(error, mediaFailureReason(error));
        playerHealth.report('anticut.createPlayerFailed', {
          reason: classification.reason,
          statusCode: classification.statusCode
        });
        if (userPausedRef.current) {
          deferFailureWhilePaused(classification);
          return;
        }
        if (!classification.retryable) makeTerminal(classification);
        else scheduleRestartRef.current(classification.reason);
      }
    },
    [
      audio,
      bindPlayer,
      bindVideoElement,
      clearRestartTimer,
      destroyPlayer,
      deferFailureWhilePaused,
      handleAutoplayBlocked,
      makeTerminal,
      onRefreshVariant,
      updatePlayback,
      variant,
      viewId
    ]
  );
  createEngineRef.current = createEngine;

  const armRestart = useCallback(
    (delayMs, reason) => {
      clearRestartTimer();
      retryReasonRef.current = reason;
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      const sessionGeneration = sessionGenerationRef.current;
      const retryAt = Date.now() + delayMs;
      updatePlayback({status: PLAYER_STATES.WAITING_RETRY, retryAt, reason});
      restartTimerRef.current = window.setTimeout(() => {
        restartTimerRef.current = null;
        if (sessionGeneration !== sessionGenerationRef.current) return;
        if (userPausedRef.current) {
          deferredFailureRef.current = {retryable: true, reason, statusCode: null};
          updatePlayback({status: PLAYER_STATES.PAUSED, retryAt: null, reason: null});
          return;
        }
        playerHealth.report('anticut.restarted', {reason, attempt: attemptRef.current});
        createEngineRef.current(playURL);
      }, delayMs);
    },
    [clearRestartTimer, playURL, updatePlayback]
  );

  const scheduleRestart = useCallback(
    (reason) => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (userPausedRef.current) {
        deferFailureWhilePaused({retryable: true, reason});
        return;
      }
      if (restartTimerRef.current) return;
      const healthyMs = healthySinceRef.current ? Date.now() - healthySinceRef.current : 0;
      const next = nextAttempt(attemptRef.current, healthyMs);
      attemptRef.current = next;
      needsRecoveryConfirmationRef.current = true;
      healthySinceRef.current = null;
      destroyPlayer();
      const delayMs = computeBackoffDelay(next);
      updatePlayback({attempt: next});
      playerHealth.report('player.retryScheduled', {variant, reason, attempt: next, delayMs});
      playerHealth.report('anticut.restartScheduled', {reason, attempt: next, delayMs, healthyMs});
      armRestart(delayMs, reason);
    },
    [armRestart, deferFailureWhilePaused, destroyPlayer, updatePlayback, variant]
  );
  scheduleRestartRef.current = scheduleRestart;

  const restartNow = useCallback(
    (reason = 'manual') => {
      clearRestartTimer();
      if (initialTimerRef.current) window.clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
      playerHealth.report('player.retryNow', {variant, reason, attempt: attemptRef.current});
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      createEngineRef.current(playURL);
    },
    [clearRestartTimer, playURL, updatePlayback, variant]
  );
  restartNowRef.current = restartNow;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const onPlaying = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (userPausedRef.current) {
        video.pause();
        updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
        return;
      }
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, reason: 'offline'});
        return;
      }
      autoplayBlockedRef.current = false;
      watchdogRef.current = createWatchdog(Date.now());
      watchdogRef.current.firstFrameAt = Date.now();
      watchdogRef.current.lastCurrentTime = video.currentTime;
      healthySinceRef.current = Date.now();
      if (
        needsRecoveryConfirmationRef.current ||
        playbackRef.current.status === PLAYER_STATES.BUFFERING
      ) {
        showRecovered();
      } else {
        updatePlayback({status: PLAYER_STATES.PLAYING, retryAt: null, reason: null});
      }
    };
    const onWaiting = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (
        !userPausedRef.current &&
        onlineRef.current &&
        playbackRef.current.status === PLAYER_STATES.PLAYING
      ) {
        updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'stall'});
      }
    };
    const onEnded = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (!userPausedRef.current) scheduleRestartRef.current('streamEnded');
    };
    const onError = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (!userPausedRef.current) scheduleRestartRef.current('network');
    };

    video.addEventListener('playing', onPlaying);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onWaiting);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onWaiting);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
  }, [showRecovered, updatePlayback]);

  useEffect(() => {
    watchdogTimerRef.current = window.setInterval(() => {
      const video = videoRef.current;
      const watchdog = watchdogRef.current;
      if (
        !video ||
        !watchdog ||
        !playerRef.current ||
        restartTimerRef.current ||
        userPausedRef.current ||
        autoplayBlockedRef.current ||
        !onlineRef.current
      ) {
        return;
      }
      const verdict = evaluateWatchdog(
        watchdog,
        {
          now: Date.now(),
          currentTime: video.currentTime,
          paused: false,
          ended: false,
          visible: typeof document === 'undefined' || document.visibilityState !== 'hidden'
        },
        {startupTimeoutMs: STARTUP_TIMEOUT_MS, stallThresholdMs: STALL_THRESHOLD_MS}
      );
      if (verdict.action === 'restart') {
        const event =
          verdict.reason === 'startupTimeout' ? 'anticut.startupTimeout' : 'anticut.stallDetected';
        playerHealth.report(event, {readyState: video.readyState});
        scheduleRestartRef.current(verdict.reason);
      }
    }, WATCHDOG_INTERVAL_MS);
    return () => {
      if (watchdogTimerRef.current) window.clearInterval(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handleOffline = () => {
      onlineRef.current = false;
      setOnline(false);
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      clearRestartTimer();
      if (recoveredTimerRef.current) window.clearTimeout(recoveredTimerRef.current);
      updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
      playerHealth.report('player.offline', {variant});
    };
    const handleOnline = () => {
      onlineRef.current = true;
      setOnline(true);
      playerHealth.report('player.online', {variant});
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      const video = videoRef.current;
      if (userPausedRef.current) {
        updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
      } else if (
        playerRef.current &&
        video &&
        !video.ended &&
        !video.error &&
        video.readyState >= 2
      ) {
        showRecovered();
      } else {
        restartNowRef.current('online');
      }
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [clearRestartTimer, showRecovered, updatePlayback, variant]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleSlotAvailable = () => {
      const currentStatus = playbackRef.current.status;
      if (
        userPausedRef.current ||
        currentStatus === PLAYER_STATES.PAUSED ||
        currentStatus === PLAYER_STATES.TERMINAL_ERROR
      ) {
        playerHealth.report('anticut.slotAvailable.ignored', {
          reason: currentStatus === PLAYER_STATES.TERMINAL_ERROR ? 'terminalError' : 'userPaused'
        });
        return;
      }
      // slotAvailable is only an accelerator for a transport that has already
      // failed and been destroyed. A delayed websocket event must never tear
      // down a current connecting, buffering or playing generation.
      if (playerRef.current && !restartTimerRef.current) {
        playerHealth.report('anticut.slotAvailable.ignored', {reason: 'transportIsActive'});
        return;
      }
      // The payload contains only publicPath (no view/channel id), so it cannot
      // authorize a restart in another tab. Recovery is driven by the EOF/error
      // observed by this transport; keep the websocket signal advisory.
      playerHealth.report('anticut.slotAvailable.ignored', {reason: 'uncorrelatedSignal'});
    };
    socket.on('slotAvailable', handleSlotAvailable);
    return () => {
      socket.off('slotAvailable', handleSlotAvailable);
    };
  }, [socket]);

  useEffect(() => {
    sessionGenerationRef.current += 1;
    const sessionGeneration = sessionGenerationRef.current;
    attemptRef.current = 0;
    healthySinceRef.current = null;
    autoplayBlockedRef.current = false;
    deferredFailureRef.current = null;
    needsRecoveryConfirmationRef.current = false;
    clearRestartTimer();
    destroyPlayer();
    updatePlayback({
      status: PLAYER_STATES.PREPARING,
      attempt: 0,
      retryAt: null,
      reason: null
    });
    initialTimerRef.current = window.setTimeout(() => {
      initialTimerRef.current = null;
      if (sessionGeneration !== sessionGenerationRef.current) return;
      if (onlineRef.current) createEngineRef.current(playURL);
      else updatePlayback({status: PLAYER_STATES.OFFLINE, reason: 'offline'});
    }, INITIAL_CONNECT_DELAY_MS);
    return () => {
      sessionGenerationRef.current += 1;
      if (initialTimerRef.current) window.clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
      clearRestartTimer();
      if (recoveredTimerRef.current) window.clearTimeout(recoveredTimerRef.current);
      recoveredTimerRef.current = null;
      destroyPlayer();
    };
  }, [clearRestartTimer, destroyPlayer, playURL, updatePlayback, variant]);

  const requestPause = useCallback(() => {
    if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
    userPausedRef.current = true;
    autoplayBlockedRef.current = false;
    if (restartTimerRef.current) {
      deferredFailureRef.current = {
        retryable: true,
        reason: retryReasonRef.current || 'network',
        statusCode: null
      };
      clearRestartTimer();
    }
    onUserPausedChange(true);
    videoRef.current?.pause();
    updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
    playerHealth.report('player.userPause', {variant});
    playerHealth.report('anticut.userPause', {});
  }, [clearRestartTimer, onUserPausedChange, updatePlayback, variant]);

  const requestPlay = useCallback(() => {
    if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
    userPausedRef.current = false;
    autoplayBlockedRef.current = false;
    onUserPausedChange(false);
    playerHealth.report('player.userPlay', {variant});
    playerHealth.report('anticut.userPlay', {});
    const deferredFailure = deferredFailureRef.current;
    deferredFailureRef.current = null;
    if (deferredFailure) {
      if (!deferredFailure.retryable) {
        makeTerminal(deferredFailure);
      } else {
        restartNowRef.current(`resume:${deferredFailure.reason}`);
      }
      return;
    }
    const video = videoRef.current;
    if (!playerRef.current || playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) {
      restartNowRef.current('userPlay');
      return;
    }
    const promise = video?.play();
    if (promise && typeof promise.catch === 'function') promise.catch(handleAutoplayBlocked);
  }, [handleAutoplayBlocked, makeTerminal, onUserPausedChange, variant]);

  const selectAudioTrack = (id) => {
    if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
    if (deferredFailureRef.current && !deferredFailureRef.current.retryable) return;
    if (audio.select(id)) return;
    if (userPausedRef.current) {
      deferredFailureRef.current = {retryable: true, reason: 'audioTrack', statusCode: null};
      return;
    }
    restartNowRef.current('audioTrack');
  };

  return (
    <PlayerChrome
      containerRef={containerRef}
      videoRef={videoRef}
      audioTracks={audioTracks}
      selectedAudioTrack={selectedAudioTrack}
      onAudioTrackChange={selectAudioTrack}
      status={playback.status}
      variant={variant}
      playingName={playingName}
      attempt={playback.attempt}
      retryAt={playback.retryAt}
      reason={playback.reason}
      userPaused={userPaused}
      online={online}
      onRequestPlay={requestPlay}
      onRequestPause={requestPause}
      onRetryNow={() => restartNow('manual')}
    >
      <video
        ref={videoRef}
        playsInline
        preload="auto"
        style={{width: '100%', height: '100%', display: 'block', objectFit: 'contain'}}
      >
        Tu navegador no permite reproducir vídeo.
      </video>
    </PlayerChrome>
  );
}

AntiCutPlayer.propTypes = {
  playURL: PropTypes.string.isRequired,
  playingName: PropTypes.string,
  variant: PropTypes.oneOf(['A', 'B']).isRequired,
  viewId: PropTypes.string.isRequired,
  socket: PropTypes.shape({
    on: PropTypes.func.isRequired,
    off: PropTypes.func.isRequired
  }),
  bindPlayer: PropTypes.func,
  bindVideoElement: PropTypes.func,
  userPaused: PropTypes.bool.isRequired,
  onUserPausedChange: PropTypes.func.isRequired,
  onRefreshVariant: PropTypes.func
};

export default AntiCutPlayer;
